import React, { useRef, useState } from 'react';
import ConfirmModal from '../../../components/common/ConfirmModal/ConfirmModal';
import Icon from '../../../components/common/Icon/Icon';
import PhotoFullscreenModal from '../../../components/common/PhotoFullscreenModal/PhotoFullscreenModal';
import type { MedicalExamFile } from '../../../types/medicalExams';
import {
  ACCEPTED_MEDICAL_EXAM_ACCEPT_ATTR,
  MAX_MEDICAL_EXAM_FILES,
} from '../medicalExams.constants';
import {
  createLocalExamFile,
  isAcceptedMedicalExamFile,
  revokeLocalExamFile,
} from '../medicalExams.utils';
import ExamFileCard from './ExamFileCard';
import styles from './ExamFileUploadField.module.css';

interface ExamFileUploadFieldProps {
  files: MedicalExamFile[];
  onChange: (files: MedicalExamFile[]) => void;
  dateErrors?: Record<string, string>;
  disabled?: boolean;
}

const ExamFileUploadField: React.FC<ExamFileUploadFieldProps> = ({
  files,
  onChange,
  dateErrors = {},
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewFile, setPreviewFile] = useState<MedicalExamFile | null>(null);
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);

  const updateExamDate = (fileId: string, examDate: string) => {
    onChange(files.map((item) => (
      item.id === fileId ? { ...item, examDate } : item
    )));
  };

  const canAddMore = files.length < MAX_MEDICAL_EXAM_FILES;
  const remainingSlots = MAX_MEDICAL_EXAM_FILES - files.length;

  const addFiles = (incoming: File[]) => {
    const accepted = incoming.filter(isAcceptedMedicalExamFile);
    if (accepted.length === 0) {
      window.alert('Envie arquivos em PDF, JPG ou PNG.');
      return;
    }

    if (remainingSlots <= 0) {
      window.alert(`Você pode enviar no máximo ${MAX_MEDICAL_EXAM_FILES} arquivos.`);
      return;
    }

    const nextFiles = accepted.slice(0, remainingSlots).map(createLocalExamFile);
    onChange([...files, ...nextFiles]);

    if (accepted.length > remainingSlots) {
      window.alert(`Só foi possível adicionar ${remainingSlots} arquivo(s). Limite de ${MAX_MEDICAL_EXAM_FILES}.`);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (selected.length > 0) {
      addFiles(selected);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled && canAddMore) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    if (disabled || !canAddMore) {
      return;
    }

    const dropped = Array.from(event.dataTransfer.files ?? []);
    if (dropped.length > 0) {
      addFiles(dropped);
    }
  };

  const removeFile = (fileId: string) => {
    const target = files.find((item) => item.id === fileId);
    if (target) {
      revokeLocalExamFile(target);
    }

    onChange(files.filter((item) => item.id !== fileId));
    setPendingRemoveId(null);
  };

  const handlePreview = (file: MedicalExamFile) => {
    if (file.kind === 'pdf') {
      window.open(file.previewUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    setPreviewFile(file);
  };

  return (
    <div className={styles.field}>
      <button
        type="button"
        className={`${styles.dropzone} ${isDragging ? styles.dropzoneActive : ''}`.trim()}
        disabled={disabled || !canAddMore}
        onClick={() => inputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Icon name="upload_file" size={22} color="var(--color-text-muted)" />
        <span className={styles.dropzoneCopy}>
          <span className={styles.dropzoneTitle}>
            {isDragging ? 'Solte os arquivos aqui' : 'Adicionar exames'}
          </span>
          <span className={styles.dropzoneHint}>
            PDF, JPG ou PNG · arraste ou selecione · até {MAX_MEDICAL_EXAM_FILES} arquivos
          </span>
        </span>
        <span className={styles.dropzoneAction}>Selecionar</span>
      </button>

      <input
        ref={inputRef}
        type="file"
        className={styles.hiddenInput}
        accept={ACCEPTED_MEDICAL_EXAM_ACCEPT_ATTR}
        multiple
        disabled={disabled || !canAddMore}
        onChange={handleInputChange}
      />

      {files.length > 0 && (
        <div className={styles.list}>
          {files.map((file) => (
            <ExamFileCard
              key={file.id}
              file={file}
              dateError={dateErrors[file.id]}
              disabled={disabled}
              onPreview={() => handlePreview(file)}
              onRemove={() => setPendingRemoveId(file.id)}
              onExamDateChange={(examDate) => updateExamDate(file.id, examDate)}
            />
          ))}
        </div>
      )}

      <PhotoFullscreenModal
        open={Boolean(previewFile)}
        src={previewFile?.previewUrl ?? ''}
        alt={previewFile?.fileName}
        onClose={() => setPreviewFile(null)}
      />

      <ConfirmModal
        open={Boolean(pendingRemoveId)}
        title="Remover arquivo"
        message="Deseja remover este exame da lista?"
        confirmLabel="Remover"
        cancelLabel="Cancelar"
        variant="danger"
        onCancel={() => setPendingRemoveId(null)}
        onConfirm={() => {
          if (pendingRemoveId) {
            removeFile(pendingRemoveId);
          }
        }}
      />
    </div>
  );
};

export default ExamFileUploadField;
